import { EmotionCache } from '@emotion/utils'
import { NextPage } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { ReactElement, ReactNode } from 'react'
import { ThemeProvider } from 'src/components/theme'
import { IS_LOCAL, SEGMENT_API_DOMAIN, SEGMENT_CDNG_DOMAIN, SEGMENT_CDN_DOMAIN } from 'src/config'
import { newRelicScript } from 'src/utils/newrelic'
import { LicenseInfo } from '@mui/x-license-pro'

const muiDataGridKey =
  '59c1c5d40b426196e222e00550d4d0ccTz04ODQ4MCxFPTE3NDQ4Mjk3ODcwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='

LicenseInfo.setLicenseKey(muiDataGridKey)

const EXCLUDED_ANALYTICS_PATHS = ['reports/presentation/print', 'match-analysis-print']

const googleAnalyticsCode = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());

    gtag('config', 'G-V1Z1N7ZKFP');
    window.GA_MEASUREMENT_ID = 'G-V1Z1N7ZKFP';
  `

const segmentCode = `
  !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://${SEGMENT_CDN_DOMAIN}/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="9BKeFT1C2vDOpzaVqhVLlVFTtkIAPmKg";;analytics.SNIPPET_VERSION="4.15.3";
  analytics._cdn = 'https://${SEGMENT_CDNG_DOMAIN}';
  analytics.load("9BKeFT1C2vDOpzaVqhVLlVFTtkIAPmKg", { integrations: { "Segment.io": { apiHost: "${SEGMENT_API_DOMAIN}/v1" }}});
  // analytics.page();
  }}();
`

export type NextPageWithLayout<P = any, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type MyAppProps = AppProps & {
  emotionCache: EmotionCache
  Component: NextPageWithLayout
}

const Header = () => {
  return (
    <Head>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" />
      <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
      <title>AlphaMap</title>
    </Head>
  )
}

const AnalyticsScripts = () => {
  return (
    <>
      <Script async src="https://www.googletagmanager.com/gtag/js?id=G-V1Z1N7ZKFP"></Script>
      <Script id="segment" strategy="beforeInteractive">
        {segmentCode}
      </Script>
      <Script id="gal" strategy="beforeInteractive">
        {googleAnalyticsCode}
      </Script>
      <Script id="newrelic">{newRelicScript}</Script>
    </>
  )
}

const MyApp = (props: MyAppProps) => {
  const { Component, pageProps, router } = props
  // Disable analytics for certain pages and Local Environment
  const useAnalytics = !EXCLUDED_ANALYTICS_PATHS.some((path) => router.pathname.includes(path)) && !IS_LOCAL

  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <Header />
      {useAnalytics && <AnalyticsScripts />}
      <ThemeProvider>{getLayout(<Component {...pageProps} />)}</ThemeProvider>
    </>
  )
}

export default MyApp
